/**
 * Query keys for the owner (lessor) detail aggregate, and the one invalidation
 * every owner write goes through.
 *
 * Kept in their own module so surfaces that merely need to INVALIDATE owner
 * detail (e.g. the management-invoice mutations) don't have to import the whole
 * useOwnerDetail hook — and can't create an import cycle with it.
 */
import type { QueryClient } from "@tanstack/react-query";

/** Prefix matching every owner-detail query — invalidate to refresh them all. */
export const OWNER_DETAIL_PREFIX = ["v2", "owner-detail"] as const;

export const OWNER_DETAIL_KEY = (lessorId: string | null) =>
  [...OWNER_DETAIL_PREFIX, lessorId ?? ""] as const;

/** Prefix matching every owner Activity trail. */
export const OWNER_AUDIT_PREFIX = ["v2", "owner-audit"] as const;

/**
 * One owner's Activity trail. Lives here rather than beside its fetcher in
 * useOwnerDetail so the invalidation below can name it without importing that
 * module back (which imports this one) — the cycle this file exists to avoid.
 * useOwnerDetail re-exports it, so call sites are unaffected.
 */
export const OWNER_AUDIT_KEY = (lessorId: string | null) =>
  [...OWNER_AUDIT_PREFIX, lessorId] as const;

/**
 * Every cache that holds a copy of an owner, invalidated together.
 *
 * The same owner is cached under FIVE unrelated key prefixes, because each
 * surface reads it differently: `owners-list` enriches lessors with apartment
 * counts, `owner-detail` aggregates their portfolio, `lessors` is the raw row,
 * plus the audit trail and the owner-account → lessor map. Nothing in the key
 * shapes ties them together, so a mutation that names only some of them leaves
 * the rest serving the old owner until a page load.
 *
 * That is exactly what happened: useUpdateLessor invalidated `["v2","lessors"]`
 * and the audit key, but the Owners LIST reads `["v2","owners-list", search]`
 * and hands the modal its `lessor` prop from that list — so an admin saved an
 * owner, the modal kept the values they typed, and the list behind it (and the
 * next open of that modal) still showed the old name until they refreshed.
 *
 * So writes call this instead of hand-listing keys: adding a sixth owner cache
 * later means adding it here once, not auditing every mutation again.
 *
 * `lessorId` narrows the two per-owner caches when the caller knows which owner
 * moved; omit it (a create, a delete, a bulk change) to refresh them all.
 */
export function invalidateOwnerCaches(
  qc: QueryClient,
  lessorId?: string | null
): void {
  // Prefix keys — these match every search term / every owner.
  qc.invalidateQueries({ queryKey: ["v2", "owners-list"] });
  qc.invalidateQueries({ queryKey: ["v2", "lessors"] });
  // Which apartments belong to which owner can change with the owner record
  // (the map falls back to matching profiles.email → lessors.email), so a
  // renamed or re-addressed owner can move apartments between portfolios.
  qc.invalidateQueries({ queryKey: ["v2", "owner-user-lessor-map"] });
  // Same links, scoped per property (usePropertyRepresentorGaps).
  qc.invalidateQueries({ queryKey: ["v2", "property-owner-links"] });

  if (lessorId) {
    qc.invalidateQueries({ queryKey: OWNER_DETAIL_KEY(lessorId) });
    qc.invalidateQueries({ queryKey: OWNER_AUDIT_KEY(lessorId) });
  } else {
    qc.invalidateQueries({ queryKey: OWNER_DETAIL_PREFIX });
    qc.invalidateQueries({ queryKey: OWNER_AUDIT_PREFIX });
  }
}
